import React, { useState, useEffect } from "react";
import { useMutation } from "react-query";
import Fuse from "fuse.js";
import { motion } from "framer-motion";
import PlatformLogin from "./PlatformLogin";
import { ToastContainer, Slide, toast } from "react-toastify";
import {
  ArrowLeft,
  ArrowRight,
  CircleX,
  Search,
  Unplug,
  X,
} from "lucide-react";

const platforms = [
  {
    name: "PortPro",
    platformName: "PORTPRO",
    logo: "./logos/portpro.png",
    description: "Transport management",
  },
  {
    name: "Transmission",
    platformName: "FLEXPORT",
    logo: "./logos/transmission.png",
    description: "Freight forwarding portal",
  },

  {
    name: "E2Open",
    platformName: "E2OPEN",
    logo: "./logos/e2open.png",
    description: "Supply chain management",
  },
];

export default function PlatformAuthentication() {
  const [open, setOpen] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [searchResults, setSearchResults] = useState(platforms);
  const [searchTerm, setSearchTerm] = useState("");

  const fuse = new Fuse(platforms, {
    keys: ["name", "platformName", "description"],
    threshold: 0.3,
  });

  const {
    mutate: authenticatePlatform,
    isLoading,
    error,
  } = useMutation(async (platformData) => {
    const response = await fetch("http://localhost:3019/platformAuth", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(platformData),
    });
    if (!response.ok) {
      throw new Error("Authentication failed");
    }
    return response.json();
  });

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults(platforms);
    } else {
      const results = fuse.search(searchTerm);
      setSearchResults(results.map((result) => result.item));
    }
  }, [searchTerm]);

  return (
    <div className="relative z-10">
      <div className="fixed inset-0 bg-gray-50 bg-opacity-75 transition-opacity" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl shadow-opacity-40 transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <motion.div className="flex justify-between px-4 py-4 border-b border-slate-100 items-center">
              {selectedPlatform && (
                <div
                  onClick={() => setSelectedPlatform(null)}
                  style={{ opacity: selectedPlatform ? 1 : 0 }}
                >
                  <ArrowLeft size={18} color={"#333"} />
                </div>
              )}

              <div className="flex items-center space-around">
                <img src="./logo_bw.png" style={{ width: 90 }} />
              </div>
              <X width={16} color="#333" />
            </motion.div>
            <div className="space-y-2">
              {!selectedPlatform ? (
                <>
                  <div className="py-4 border-b border-slate-200 px-4 flex items-center justify-between">
                    <div>
                      <h1
                        as="h1"
                        className=" font-semibold leading-6 text-slate-800"
                      >
                        Create a new connection
                      </h1>
                    </div>
                    <p
                      className="text-xs text-slate-400"
                      style={{ fontSize: 10 }}
                    >
                      {platforms.length} available connections
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center px-4 border-b pb-2 bg-white shadow-sm shadow-opacity-10">
                      <Search width={16} color={"#bababa"} />
                      <input
                        id="platformSearch"
                        name="platformSearch"
                        type="text"
                        placeholder="Type to search platforms..."
                        className="block w-full text-xs border-0 text-gray-900 flex-1 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <ul
                      role="list"
                      className="divide-y divide-slate-100 overflow-y-scroll min-h-[200px] max-h-[33vh] h-1/3"
                    >
                      {searchResults.length === 0 && (
                        <div className="flex items-center justify-center flex-col py-10">
                          <Unplug color={"#cbd5e1"} size={28} />
                          <p className="text-center text-xs text-slate-300 py-2">
                            No results found
                          </p>
                          <button
                            style={{
                              color: "#FFF",
                            }}
                            className=" py-1.5 mt-5  flex hover:bg-opacity-70 bg-slate-800 items-center space-x-1.5 font-medium text-xs px-2.5 rounded-md"
                          >
                            Request integration
                          </button>
                        </div>
                      )}
                      {searchResults.map((platform) => (
                        <li
                          onClick={() => setSelectedPlatform(platform)}
                          key={platform.platformName}
                          className="flex items-center justify-between hover:bg-slate-50 hover:bg-opacity-60 cursor-pointer gap-x-6 py-3 px-4"
                        >
                          <div className="flex items-center min-w-0 gap-x-4">
                            <img
                              alt=""
                              src={platform.logo}
                              className="h-8 w-8 flex-none rounded-lg bg-slate-100"
                            />
                            <div className="min-w-0 flex-auto">
                              <p className="text-sm font-semibold leading-6 text-gray-900">
                                {platform.name}
                              </p>
                              <p className="truncate text-xs leading-5 -mt-1 text-gray-500">
                                {platform.description}
                              </p>
                            </div>
                          </div>

                          <button
                            onClick={() => setSelectedPlatform(platform)}
                            style={{
                              color: "#0F172A",
                            }}
                            className=" py-1.5 flex hover:bg-opacity-70 items-center space-x-1.5 font-medium text-xs px-2.5 rounded-md"
                          >
                            <ArrowRight size={15} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <div>
                  <div className="pt-4 px-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img
                        alt=""
                        src={selectedPlatform.logo}
                        className="h-8 w-8 flex-none rounded-lg bg-slate-100"
                      />
                      <div>
                        <h1 className="text-lg font-semibold leading-6 text-slate-800">
                          Connect to {selectedPlatform.name}
                        </h1>
                      </div>
                    </div>
                  </div>
                  <PlatformLogin toast={toast} platform={selectedPlatform} />
                </div>
              )}
            </div>
          </div>
        </div>
        <ToastContainer
          position="bottom-center"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
      </div>
    </div>
  );
}
